import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from '../../environments/environment';
import { Customer } from '../models/Customer';

export interface SalesUser {
  id: number;
  oid: string;
  firstName: string;
  lastName: string;
  email: string;
  coins: number;
  createdDate?: Date;
  changedDate?: Date;
}

export interface ChangeCoinsModel {
  coins: number;
  operation: 'add' | 'subtract';
}

export interface AddCoinsToCustomerModel {
  coins: number;
  operation: 'add' | 'subtract' | 'add-refund';
}

@Injectable({
  providedIn: 'root'
})
export default class EmployeeService {
  baseUrl = `${environment.backend.baseUrl}${environment.backend.apiUrl}/`;

  constructor(private http: HttpClient) {}

  // ============================================
  // Sales User Management
  // ============================================

  /**
   * Get all sales users
   * Replaces: GET /api/SalesUser
   */
  getSalesUsers(): Observable<SalesUser[]> {
    return this.http.get<SalesUser[]>(`${this.baseUrl}Employee/sales`);
  }

  /**
   * Get sales user by OID
   * Replaces: GET /api/SalesUser/{oid}
   */
  getSalesUserByOid(oid: string): Observable<SalesUser> {
    return this.http.get<SalesUser>(`${this.baseUrl}Employee/sales/byOid/${oid}`);
  }

  /**
   * Change sales user coins
   * Replaces: POST /api/SalesUser/changeCoins/{id}
   */
  changeSalesUserCoins(salesUserId: number, model: ChangeCoinsModel): Observable<SalesUser> {
    return this.http.post<SalesUser>(`${this.baseUrl}Employee/sales/${salesUserId}/changeCoins`, model);
  }

  /**
   * Add coins to customer (sales user operation)
   * Replaces: POST /api/SalesUser/addCoinsToCustomer/{no}
   */
  addCoinsToCustomer(customerNo: string, model: AddCoinsToCustomerModel): Observable<Customer> {
    return this.http.post<Customer>(`${this.baseUrl}Employee/sales/addCoinsToCustomer/${customerNo}`, model);
  }

  // ============================================
  // Admin Customer Operations
  // ============================================

  /**
   * Get customer detail (admin view)
   * Replaces: GET /api/Admin/{id}/detail
   */
  getCustomerDetail(customerId: number): Observable<Customer> {
    return this.http.get<Customer>(`${this.baseUrl}Employee/customer/${customerId}/detail`);
  }

  /**
   * Enable or disable customer in Entra ID
   * Replaces: POST /api/Admin/{id}/entra/{action}
   */
  setCustomerEntraUserEnabledState(customerId: number, action: 'enable' | 'disable'): Observable<Customer[]> {
    return this.http.post<Customer[]>(`${this.baseUrl}Employee/customer/${customerId}/entra/${action}`, {});
  }

  /**
   * Link OID to customer (admin override)
   * Replaces: POST /api/Admin/linkOid/{id}
   */
  linkOidToCustomer(customerId: number, oid: string): Observable<Customer> {
    return this.http.post<Customer>(`${this.baseUrl}Employee/customer/linkOid/${customerId}`, { oid });
  }
}
